import { normalizeUrlProtocol } from './url.ts';

export type VideoProvider = 'youtube' | 'vimeo' | 'loom' | 'tiktok' | 'kinescope';
export type AudioProvider = 'spotify' | 'applePodcasts' | 'soundcloud' | 'podigee' | 'mixcloud';
export type MediaProvider = VideoProvider | AudioProvider;

const YOUTUBE_HOSTS = new Set([
    'youtube.com',
    'www.youtube.com',
    'm.youtube.com',
    'youtube-nocookie.com',
    'www.youtube-nocookie.com',
]);
const YOUTUBE_SHORT_HOSTS = new Set(['youtu.be', 'www.youtu.be']);
const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com']);
const VIMEO_PLAYER_HOST = 'player.vimeo.com';
const LOOM_HOSTS = new Set(['loom.com', 'www.loom.com']);
// Kinescope uses numeric, short UUID, and UUID video IDs in share and embed links.
const KINESCOPE_HOST = 'kinescope.io';
const KINESCOPE_VIDEO_PATH = /^\/(?:embed\/)?(?:\d+|[A-Za-z0-9]{22}|[a-f\d]{8}(?:-[a-f\d]{4}){3}-[a-f\d]{12})\/?$/i;
const TIKTOK_HOSTS = new Set(['tiktok.com', 'www.tiktok.com']);
const SPOTIFY_HOSTS = new Set(['open.spotify.com', 'spotify.link']);
const APPLE_PODCASTS_HOST = 'podcasts.apple.com';
const SOUNDCLOUD_HOSTS = new Set(['soundcloud.com', 'www.soundcloud.com', 'on.soundcloud.com']);
const MIXCLOUD_HOSTS = new Set(['mixcloud.com', 'www.mixcloud.com']);
const YOUTUBE_VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_VIDEO_ID_PATTERN = /^\d+$/;
const LOOM_VIDEO_ID_PATTERN = /^[a-f\d]{32}$/i;
const TIKTOK_VIDEO_ID_PATTERN = /^\d+$/;
const SPOTIFY_CONTENT_ID_PATTERN = /^[A-Za-z0-9]{22}$/;
const SPOTIFY_LOCALE_PATTERN = /^intl-[a-z]{2}(?:-[a-z]{2})?$/i;
const SPOTIFY_CONTENT_TYPES = new Set(['show', 'episode', 'track', 'album', 'playlist', 'artist']);

const hasPathValue = (pathname: string, prefix: string): boolean => {
    const value = pathname.match(new RegExp(`^${prefix}([A-Za-z0-9_-]{11})/?$`))?.[1];
    return Boolean(value && YOUTUBE_VIDEO_ID_PATTERN.test(value));
};

// Vimeo publishes several URL schemes; match each documented scheme without accepting arbitrary numeric path endings.
const getVimeoVideoID = (pathname: string): string | null => {
    const patterns = [
        /^\/(\d+)(?:\/[A-Za-z0-9]+)?\/?$/,
        /^\/album\/\d+\/video\/(\d+)\/?$/,
        /^\/channels\/[A-Za-z0-9_-]+\/(\d+)\/?$/,
        /^\/groups\/[A-Za-z0-9_-]+\/videos\/(\d+)\/?$/,
        /^\/ondemand\/[A-Za-z0-9_-]+\/(\d+)\/?$/,
    ];
    const videoID = patterns.map((pattern) => pathname.match(pattern)?.[1]).find(Boolean);
    return videoID && VIMEO_VIDEO_ID_PATTERN.test(videoID) ? videoID : null;
};

// Spotify share links may prefix the canonical content path with a locale such as `intl-de`.
const getSpotifyContentSegments = (pathname: string): string[] => {
    const pathSegments = pathname.split('/').filter(Boolean);
    return SPOTIFY_LOCALE_PATTERN.test(pathSegments[0] || '') ? pathSegments.slice(1) : pathSegments;
};

const hasValidSpotifyContentPath = (pathname: string): boolean => {
    const pathSegments = getSpotifyContentSegments(pathname);
    return (
        pathSegments.length === 2 &&
        SPOTIFY_CONTENT_TYPES.has(pathSegments[0]) &&
        SPOTIFY_CONTENT_ID_PATTERN.test(pathSegments[1])
    );
};

const getTikTokVideoID = (value: string): string | null => {
    try {
        const url = new URL(normalizeUrlProtocol(value));
        if (url.protocol !== 'https:' || !TIKTOK_HOSTS.has(url.hostname.toLowerCase())) {
            return null;
        }

        const videoID = url.pathname.match(/^\/@[A-Za-z0-9._]+\/video\/(\d+)\/?$/)?.[1];
        return videoID && TIKTOK_VIDEO_ID_PATTERN.test(videoID) ? videoID : null;
    } catch (error) {
        return null;
    }
};

// Identify only supported provider URLs so look-alike domains cannot reach oEmbed.
export const getVideoProvider = (value: string): VideoProvider | null => {
    if (!value) {
        return null;
    }

    try {
        const url = new URL(normalizeUrlProtocol(value));
        const hostname = url.hostname.toLowerCase();

        if (url.protocol !== 'https:') {
            return null;
        }

        if (YOUTUBE_SHORT_HOSTS.has(hostname)) {
            const videoID = url.pathname.split('/').filter(Boolean)[0];
            return videoID && YOUTUBE_VIDEO_ID_PATTERN.test(videoID) ? 'youtube' : null;
        }

        if (YOUTUBE_HOSTS.has(hostname)) {
            if (url.pathname === '/watch') {
                const videoID = url.searchParams.get('v');
                return videoID && YOUTUBE_VIDEO_ID_PATTERN.test(videoID) ? 'youtube' : null;
            }

            if (url.pathname.startsWith('/shorts/') && hasPathValue(url.pathname, '/shorts/')) {
                return 'youtube';
            }

            if (url.pathname.startsWith('/embed/') && hasPathValue(url.pathname, '/embed/')) {
                return 'youtube';
            }

            return null;
        }

        if (VIMEO_HOSTS.has(hostname)) {
            return getVimeoVideoID(url.pathname) ? 'vimeo' : null;
        }

        if (hostname === VIMEO_PLAYER_HOST) {
            const videoID = url.pathname.match(/^\/video\/(\d+)\/?$/)?.[1];
            return videoID ? 'vimeo' : null;
        }

        if (LOOM_HOSTS.has(hostname)) {
            const videoID = url.pathname.match(/^\/(?:share|embed)\/([a-f\d]{32})\/?$/i)?.[1];
            return videoID && LOOM_VIDEO_ID_PATTERN.test(videoID) ? 'loom' : null;
        }

        if (hostname === KINESCOPE_HOST) {
            return KINESCOPE_VIDEO_PATH.test(url.pathname) ? 'kinescope' : null;
        }

        if (TIKTOK_HOSTS.has(hostname)) {
            return getTikTokVideoID(value) ? 'tiktok' : null;
        }
    } catch (error) {
        return null;
    }

    return null;
};

export const isValidVideoUrl = (value: string): boolean => Boolean(getVideoProvider(value));

export const getAudioProvider = (value: string): AudioProvider | null => {
    if (!value) {
        return null;
    }

    try {
        const url = new URL(normalizeUrlProtocol(value));
        const hostname = url.hostname.toLowerCase();
        const pathSegments = url.pathname.split('/').filter(Boolean);

        if (url.protocol !== 'https:') {
            return null;
        }

        if (SPOTIFY_HOSTS.has(hostname)) {
            if (hostname === 'spotify.link') {
                return pathSegments.length === 1 ? 'spotify' : null;
            }

            return hasValidSpotifyContentPath(url.pathname) ? 'spotify' : null;
        }

        if (hostname === APPLE_PODCASTS_HOST) {
            const podcastIndex = pathSegments.indexOf('podcast');
            const contentID = pathSegments[pathSegments.length - 1];
            const episodeID = url.searchParams.get('i');
            return (podcastIndex === 0 || podcastIndex === 1) &&
                /^id\d+$/.test(contentID) &&
                (!url.searchParams.has('i') || Boolean(episodeID && /^\d+$/.test(episodeID)))
                ? 'applePodcasts'
                : null;
        }

        if (SOUNDCLOUD_HOSTS.has(hostname)) {
            // SoundCloud oEmbed supports user profiles in addition to tracks and playlists.
            return pathSegments.length >= 1 ? 'soundcloud' : null;
        }

        if (hostname.endsWith('.podigee.io') && hostname !== 'podigee.io') {
            return pathSegments.length >= 1 ? 'podigee' : null;
        }

        if (MIXCLOUD_HOSTS.has(hostname)) {
            return pathSegments.length >= 2 ? 'mixcloud' : null;
        }
    } catch (error) {
        return null;
    }

    return null;
};

export const isValidAudioUrl = (value: string): boolean => Boolean(getAudioProvider(value));
export const getMediaProvider = (value: string): MediaProvider | null =>
    getVideoProvider(value) || getAudioProvider(value);
export const isValidMediaUrl = (value: string): boolean => Boolean(getMediaProvider(value));

export const normalizeVideoUrl = (value: string): string => {
    const normalizedUrl = normalizeUrlProtocol(value);
    return isValidVideoUrl(normalizedUrl) ? normalizedUrl : value;
};

export const normalizeMediaUrl = (value: string): string => {
    const normalizedUrl = normalizeUrlProtocol(value);
    if (!isValidMediaUrl(normalizedUrl)) {
        return value;
    }

    const url = new URL(normalizedUrl);
    if (url.hostname.toLowerCase() === 'open.spotify.com' && SPOTIFY_LOCALE_PATTERN.test(url.pathname.split('/')[1])) {
        // Persist and submit Spotify's locale-independent form so shared links remain stable across regions.
        url.pathname = `/${getSpotifyContentSegments(url.pathname).join('/')}`;
        return url.toString();
    }

    return normalizedUrl;
};

export const getVideoOEmbedUrl = (value: string): string | null => {
    const provider = getVideoProvider(value);

    if (provider === 'youtube') {
        return `https://www.youtube.com/oembed?url=${encodeURIComponent(normalizeVideoUrl(value))}&format=json`;
    }

    if (provider === 'vimeo') {
        return `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(normalizeVideoUrl(value))}`;
    }

    if (provider === 'loom') {
        return `https://www.loom.com/v1/oembed?url=${encodeURIComponent(normalizeVideoUrl(value))}&format=json`;
    }

    // Resolve Kinescope through its public oEmbed endpoint, like the existing video providers.
    if (provider === 'kinescope') {
        return `https://kinescope.io/oembed?url=${encodeURIComponent(normalizeVideoUrl(value))}&format=json`;
    }

    if (provider === 'tiktok') {
        return `https://www.tiktok.com/oembed?url=${encodeURIComponent(normalizeVideoUrl(value))}`;
    }

    return null;
};

export const getAudioOEmbedUrl = (value: string): string | null => {
    const provider = getAudioProvider(value);
    const normalizedUrl = normalizeMediaUrl(value);
    const encodedUrl = encodeURIComponent(normalizedUrl);

    if (provider === 'spotify') {
        return `https://open.spotify.com/oembed?url=${encodedUrl}`;
    }

    if (provider === 'soundcloud') {
        return `https://soundcloud.com/oembed?url=${encodedUrl}&format=json`;
    }

    if (provider === 'podigee') {
        return `https://embed.podigee.com/oembed?url=${encodedUrl}&type=iframe`;
    }

    if (provider === 'mixcloud') {
        return `https://app.mixcloud.com/oembed/?url=${encodedUrl}&format=json`;
    }

    return null;
};

// Apple publishes a direct official player rather than an oEmbed JSON endpoint.
export const getDirectAudioEmbedUrl = (value: string): string | null => {
    if (getAudioProvider(value) !== 'applePodcasts') {
        return null;
    }

    const url = new URL(normalizeMediaUrl(value));
    url.hostname = 'embed.podcasts.apple.com';
    return url.toString();
};

// Require a click to play videos, overriding autoplay requested by provider defaults.
const getVideoPlaybackUrl = (url: URL): string => {
    url.searchParams.set('autoplay', '0');
    return url.toString();
};

// TikTok publishes an official iframe player that can be derived safely from a validated canonical URL.
export const getDirectVideoEmbedUrl = (value: string): string | null => {
    const videoID = getVideoProvider(value) === 'tiktok' ? getTikTokVideoID(value) : null;
    return videoID ? getVideoPlaybackUrl(new URL(`https://www.tiktok.com/player/v1/${videoID}`)) : null;
};

// Accept iframe sources only from the provider selected by the persisted URL.
export const getTrustedVideoEmbedUrl = (html: string, provider: VideoProvider, sourceUrl = ''): string | null => {
    if (!html || typeof DOMParser === 'undefined') {
        return null;
    }

    const document = new DOMParser().parseFromString(html, 'text/html');

    if (provider === 'tiktok') {
        const requestedVideoID = getTikTokVideoID(sourceUrl);
        const responseVideoID = document.querySelector('blockquote.tiktok-embed')?.getAttribute('data-video-id');

        // TikTok oEmbed returns a blockquote and script, so verify its ID before creating the official iframe player URL.
        return requestedVideoID && responseVideoID === requestedVideoID ? getDirectVideoEmbedUrl(sourceUrl) : null;
    }

    const iframeSource = document.querySelector('iframe')?.getAttribute('src');
    if (!iframeSource) {
        return null;
    }

    try {
        const url = new URL(iframeSource);
        const hostname = url.hostname.toLowerCase();

        if (url.protocol !== 'https:') {
            return null;
        }

        if (
            provider === 'youtube' &&
            ['www.youtube.com', 'www.youtube-nocookie.com'].includes(hostname) &&
            url.pathname.startsWith('/embed/')
        ) {
            return getVideoPlaybackUrl(url);
        }

        if (provider === 'vimeo' && hostname === VIMEO_PLAYER_HOST && url.pathname.startsWith('/video/')) {
            return getVideoPlaybackUrl(url);
        }

        // Kinescope oEmbed returns both direct player paths and /embed/ paths.
        if (provider === 'kinescope' && hostname === KINESCOPE_HOST && KINESCOPE_VIDEO_PATH.test(url.pathname)) {
            return getVideoPlaybackUrl(url);
        }

        if (provider === 'loom' && LOOM_HOSTS.has(hostname) && /^\/embed\/[a-f\d]{32}\/?$/i.test(url.pathname)) {
            return getVideoPlaybackUrl(url);
        }
    } catch (error) {
        return null;
    }

    return null;
};

export const getTrustedAudioEmbedUrl = (html: string, provider: AudioProvider): string | null => {
    if (!html || typeof DOMParser === 'undefined') {
        return null;
    }

    const iframeSource = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('iframe')
        ?.getAttribute('src');
    if (!iframeSource) {
        return null;
    }

    try {
        const url = new URL(iframeSource);
        const hostname = url.hostname.toLowerCase();

        if (url.protocol !== 'https:') {
            return null;
        }

        if (provider === 'spotify' && hostname === 'open.spotify.com' && url.pathname.startsWith('/embed/')) {
            return url.toString();
        }

        if (provider === 'soundcloud' && hostname === 'w.soundcloud.com' && url.pathname === '/player/') {
            return url.toString();
        }

        if (provider === 'podigee' && hostname.endsWith('.podigee.io') && url.pathname.endsWith('/embed')) {
            return url.toString();
        }

        if (
            provider === 'mixcloud' &&
            ['player-widget.mixcloud.com', 'www.mixcloud.com'].includes(hostname) &&
            url.pathname.startsWith('/widget/iframe/')
        ) {
            return url.toString();
        }
    } catch (error) {
        return null;
    }

    return null;
};
